import { baseApi, RTKQueryTag } from '../../data/baseApi'

// --- Request Types ---

export type AddNewClientArgs = {
  first_name: string
  last_name: string
  email?: string
  company: string
  dob: string
  provider_id: string
  program_name: string
  pid?: string
  // Legacy-transition fields (NETEN-2547): both omitted for the normal ANC flow;
  // is_transitioning_client=false is reserved for the separate blocking-net-new flow
  patient_lyra_id?: string
  is_transitioning_client?: boolean
}

export type GetProviderProgramsArgs = {
  customer: string
  providerLyraId: string
}

export type GetClientRegistrationsArgs = {
  providerId: string
  status?: 'pending' | 'completed' | 'expired'
}

export type ResendRegistrationArgs = {
  client_registration_id: string
}

export type DeleteRegistrationArgs = {
  client_registration_id: string
}

// --- Response Types ---

export type AddNewClientResponse = {
  status: 'success' | 'error'
  error_status?: string
  message?: string
}

export type ClientRegistration = {
  id: string
  client_id: string
  first_name: string
  last_name: string
  dob: string
  email: string
  registration_status: 'pending' | 'completed' | 'expired'
  registration_date: string | null
  last_registration_link_sent: string | null
  program_name: string
}

export type GetClientRegistrationsResponse = {
  status: 'success' | 'error'
  clients: ClientRegistration[]
  message?: string
}

// --- API ---

const addNewClientApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getProviderPrograms: builder.query<string[], GetProviderProgramsArgs>({
      query: ({ customer, providerLyraId }) => ({
        method: 'get',
        url: '/v1/customers/session-types/provider-programs',
        params: { customer, provider_lyra_id: providerLyraId },
      }),
    }),
    addNewClient: builder.mutation<AddNewClientResponse, AddNewClientArgs>({
      query: (data) => ({
        method: 'post',
        url: '/v1/providers/add-new-client',
        data,
        showAlert: false,
      }),
      // A registered legacy transition adds the client straight to the active Engage list, which the CLIENT tag feeds
      invalidatesTags: (result, error, arg) =>
        arg.is_transitioning_client && !error
          ? [RTKQueryTag.NEW_CLIENTS_LIST, RTKQueryTag.CLIENT]
          : [RTKQueryTag.NEW_CLIENTS_LIST],
    }),
    getClientRegistrations: builder.query<GetClientRegistrationsResponse, GetClientRegistrationsArgs>({
      query: ({ providerId, status }) => ({
        method: 'get',
        url: `/v1/providers/add-new-client/${providerId}`,
        params: status ? { status } : undefined,
      }),
      providesTags: [RTKQueryTag.NEW_CLIENTS_LIST],
    }),
    resendRegistrationEmail: builder.mutation<AddNewClientResponse, ResendRegistrationArgs>({
      query: (data) => ({
        method: 'post',
        url: '/v1/providers/add-new-client/resend',
        data,
        showAlert: false,
      }),
      invalidatesTags: [RTKQueryTag.NEW_CLIENTS_LIST],
    }),
    deleteClientRegistration: builder.mutation<AddNewClientResponse, DeleteRegistrationArgs>({
      query: (data) => ({
        method: 'delete',
        url: '/v1/providers/add-new-client/delete',
        data,
        showAlert: false,
      }),
      invalidatesTags: [RTKQueryTag.NEW_CLIENTS_LIST],
    }),
  }),
})

export const {
  useGetProviderProgramsQuery,
  useAddNewClientMutation,
  useGetClientRegistrationsQuery,
  useResendRegistrationEmailMutation,
  useDeleteClientRegistrationMutation,
} = addNewClientApi

export default addNewClientApi
